import { View, Text, Platform } from 'react-native'
import React from 'react'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import OptionDefault, {
  OptionProps,
} from '@order/steps/templates/Default/Option/OptionDefault'
import Typography from '@ui/typography'
import { StepVariable } from '@order/steps/types'
import ButtonsGrid from '@ui/buttons/ButtonsFixed/ButtonsGrid'
import { useTranslation } from 'react-i18next'
import { getFirstExistingKey } from 'utils/i18nUtils'
import { Recommendations } from '@ca/common/types'
import FormattedText from '@ui/typography/FormattedText'

interface Props {
  open: boolean
  onAccept: () => void
  onDecline: () => void
  close: () => void
  option: OptionProps
  OptionComponent?: React.ComponentType<OptionProps>
  variable?: StepVariable | null
  recommendation: Recommendations.Option
}

const getKeys = (
  variable?: StepVariable | null | undefined,
  key?: string
): string[] => {
  return [
    `deals-recommendations:recommended.${variable}.${key}`,
    `recommendations:option.${key}`,
  ]
}

export default function OptionRecommendedModal({
  open,
  onAccept,
  onDecline,
  close,
  option,
  OptionComponent = OptionDefault,
  variable,
  recommendation,
}: Props) {
  const { t, i18n } = useTranslation(['ui', 'recommendations', 'deals-recommendations'])
  const keys = (key: string) => getKeys(variable, key)

  const tTitle = t(getFirstExistingKey(i18n, keys('title')) || '')
  const tCta = t(getFirstExistingKey(i18n, keys('cta')) || '')
  const tAccept = t(getFirstExistingKey(i18n, keys('button.accept')) || '')
  const tDecline = t(getFirstExistingKey(i18n, keys('button.decline')) || '')
  const descKey = getFirstExistingKey(
    i18n,
    keys(`${recommendation.key ?? recommendation.value}.desc`)
  )
  const tDesc = descKey ? t(descKey) : undefined

  return (
    <ModalWide
      center
      showClose
      hideBottomSpace={Platform.select({ default: false, web: true })}
      open={open}
      close={close}
      cardComponentProps={{
        justifyContent: 'flex-end',
      }}
      title={tTitle}
    >
      <View className="flex flex-col items-center gap-8 px-8">
        {OptionComponent && <OptionComponent {...option} onPress={onAccept} />}
        <View className="flex flex-col items-center gap-8 px-8 py-4">
          {!!tDesc && (
            <FormattedText
              variant="MD"
              text={tDesc}
              className="color-text/50 text-center"
              classNameBold="color-text/75"
            />
          )}
          <Typography variant="MD" className="color-desc text-center">
            {tCta}
          </Typography>
        </View>
        <View className="flex flex-row items-center justify-center gap-16">
          <ButtonsGrid
            buttons={[
              {
                variant: 'outlined',
                label: tDecline,
                onPress: onDecline,
                className: 'w-max self-end',
              },
              {
                variant: 'filled',
                label: tAccept,
                onPress: onAccept,
                iconEnd: 'arrow-right',
                className: 'w-max self-start',
              },
            ]}
          />
        </View>
      </View>
    </ModalWide>
  )
}
